import { useEffect, useMemo, useState } from "react";

import { TimeFrameType } from "src/organization/pages/project/environment/metrics/resources/types";

import { Poller } from "./poller";
import useLoadingOption from "./useLoadingOption";
import usePollerFocus from "./usePollerFocus";

type UsePollerProps<D = any, T = D> = {
  requestFn: (timeFrame: TimeFrameType | null) => Promise<D>;

  timeFrame?: TimeFrameType | null;
  start?: boolean;
  formatData?: (value: D) => T;
};

const usePoller = <D = any, T = D>(p: UsePollerProps<D, T>) => {
  /*
   * NOTE: If you're encountering an infinite loop with this hook, it's highly probable
   * that either `requestFn` or `formatData` (or both) are being passed as arrow functions
   * directly into the hook.
   *
   * React perceives arrow functions created inline during a render as new objects
   * on every render cycle. This causes the hook's dependency array to detect a change
   * each time, leading to an continuous re-execution.
   *
   * To prevent this, ensure that both `requestFn` and `formatData` are memoized.
   * The recommended approach is to wrap these functions in `useCallback`, and
   * meticulously manage their `useCallback` dependencies to ensure those
   * dependencies themselves are not causing the callback to be recreated unnecessarily.
   */

  const { requestFn, timeFrame, start = true, formatData } = p;
  const [data, onChange] = useState<T>();
  const [status, onLoadingChange] = useLoadingOption();
  const [error, onError] = useState<unknown>();
  const [lastUpdated, setLastUpdated] = useState(new Date());

  const poller = useMemo(
    () =>
      new Poller<D, T>({
        onChange: data => {
          onChange(data);
          setLastUpdated(new Date());
        },
        formatData,
        onLoadingChange: (option, timeFrame, selfManaged) => {
          onLoadingChange(option);
          const type = option.status.type;
          const isRange = !timeFrame?.id;
          if (option.isLoading || type === "refresh" || isRange) return;
          // For relative time frames identified by those having id as defind 1d:"15m" etc,
          // the timeFrame object itself doesn't change between polls. We manually
          // update a hash to force downstream dependencies (like React hooks) to
          // recognize a change and re-trigger their logic.
          // if it is notself managed, we don't need to update the hash the manager will do it
          selfManaged && (timeFrame.hash = Date.now().toString());
        },
        onError
      }),
    // ensure proper memo of formatData to avoid re-rendering
    [formatData, onLoadingChange]
  );

  useEffect(() => {
    // null is a valid timeFrame, it means the poller should be started
    // but the asyncFn will called with null timeFrame
    if ((!timeFrame && timeFrame !== null) || !start) {
      // timeFrame is undefined, so we set up the poller without starting it,
      // assuming it will be triggered by a PollGroup
      poller.setTask(requestFn);
      // notify that this poller is not loading anymore since useLoadingOption started off
      // with assuming the poller is loading
      poller.setLoadingStop({ type: "idle", isLoading: false });
      return;
    }

    timeFrame?.id
      ? poller.startPolling(requestFn, timeFrame)
      : poller.pollOnce(requestFn, timeFrame);
    // ensure proper memo of dependencies to avoid re-rendering
  }, [start, timeFrame, poller, requestFn]);

  usePollerFocus(poller);

  useEffect(() => {
    return () => {
      poller.unsubscribe();
    };
  }, [poller]);

  return { data, status, error, poller, lastUpdated };
};

export default usePoller;
